/* eslint-disable import/prefer-default-export */
/**
 * Parses encoded query parameters and calls callback function for every
 * parameter found in the string.
 *
 * Missing value of parameter (e.g. “…&key&…”) is treated as if the value was an
 * empty string.  Keys may be empty strings (e.g. “…&=value&…”) which also means
 * that “…&=&…” and “…&&…” will result in an empty key and value.
 *
 * @param {string} encodedQuery Encoded query string excluding question mark at
 *     the beginning.
 * @param {any} callback Function called for every
 *     parameter found in query string.  The first argument (name) will not be
 *     urldecoded (so the function is consistent with buildQueryData), but the
 *     second will.  If the parameter has no value (i.e. “=” was not present)
 *     the second argument (value) will be an empty string.
 */
export function parseQueryData(
  encodedQuery: string,
  callback: (name: string, value: string) => void
): void {
  if (!encodedQuery) {
    return
  }
  const pairs = encodedQuery.split('&')
  for (let i = 0; i < pairs.length; i += 1) {
    const indexOfEquals = pairs[i].indexOf('=')
    let name: string | null = null
    let value: string | null = null
    if (indexOfEquals >= 0) {
      name = pairs[i].substring(0, indexOfEquals)
      value = pairs[i].substring(indexOfEquals + 1)
    } else {
      name = pairs[i]
    }
    callback(name, value ? decodeURIComponent(value.replace(/\+/g, ' ')) : '')
  }
}

export interface QueryDataType {
  keyMap: Map<string, any[]> | null
  count: number | null
  encodedQuery: string | null
  ignoreCase: boolean
  clone: () => QueryDataType
  add: (key: string, value: any) => QueryDataType
  getKeyName: (key: string) => string
  invalidateCache: () => void
  ensureKeyMapInitialized: () => void
  containsKey: (key: string) => boolean
  getValues: (key: string) => any[]
  toString: () => string
}

/**
 * NOTE: Copy from https://github.com/google/closure-library/blob/6da97fe1a4267cce7c79b5876369dea7149ad689/closure/goog/uri/uri.js#L1096
 * Class used to represent URI query parameters.  It is essentially a hash of
 * name-value pairs, though a name can be present more than once.
 *
 * Has the same interface as the collections in goog.structs.
 *
 * @param {?string=} query Optional encoded query string to parse into
 *     the object.
 * @param {boolean=} ignoreCase If true, ignore the case of the parameter
 *     name in #get.
 */
export const QueryData = (query?: string, ignoreCase?: boolean): QueryDataType => {
  const state: QueryDataType = {
    keyMap: null,
    count: null,
    encodedQuery: query || null,
    ignoreCase: !!ignoreCase,
    clone: () => {
      const rv = QueryData()
      rv.encodedQuery = state.encodedQuery
      if (state.keyMap) {
        rv.keyMap = new Map(state.keyMap)
        rv.count = state.count
      }
      return rv
    },
    add: (key, value) => {
      state.ensureKeyMapInitialized()
      state.invalidateCache()
      const keyName = state.getKeyName(key)
      let values = state.keyMap?.get(keyName)
      if (!values) {
        values = []
        state.keyMap?.set(keyName, values)
      }
      values.push(value)
      state.count = (state.count || 0) + 1
      return state
    },
    getKeyName: (arg) => {
      const keyName = String(arg)
      return state.ignoreCase ? keyName.toLowerCase() : keyName
    },
    invalidateCache: () => {
      state.encodedQuery = null
    },
    ensureKeyMapInitialized: () => {
      // can access state directly
      if (!state.keyMap) {
        state.keyMap = new Map()
        state.count = 0
        if (state.encodedQuery) {
          parseQueryData(state.encodedQuery, (name, value) => {
            state.add(decodeURIComponent(name.replace(/\+/g, ' ')), value)
          })
        }
      }
    },
    containsKey: (key) => {
      state.ensureKeyMapInitialized()
      const keyName = state.getKeyName(key)
      return state.keyMap?.has(keyName) || false
    },
    getValues: (key) => {
      state.ensureKeyMapInitialized()
      let rv: any[] = []
      if (typeof key === 'string') {
        if (state.containsKey(key)) {
          rv = rv.concat(state.keyMap?.get(state.getKeyName(key)))
        }
      } else {
        // Return all values.
        const values = state.keyMap?.values() || []
        rv = Array.from(values)
      }
      return rv
    },
    toString: () => {
      if (state.encodedQuery) {
        return state.encodedQuery
      }

      if (!state.keyMap) {
        return ''
      }

      const sb: string[] = []

      // Use forEach to loop through keyMap
      state.keyMap.forEach((value, key) => {
        const encodedKey = encodeURIComponent(String(key))
        const valueList = state.getValues(key)
        valueList.forEach((val) => {
          let param = encodedKey
          // Ensure that null and undefined are encoded into the url as
          // literal strings.
          if (val !== '') param += `=${encodeURIComponent(String(val))}`
          sb.push(param)
        })
      })

      state.encodedQuery = sb.join('&')
      return state.encodedQuery
    }
  }

  return state
}
